import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { isEmpty } from 'lodash';
import { TZDate } from '@date-fns/tz';
import { computeMonthEndDate } from '../utils';
import { ProviderType } from '../../../api/types';
import { useDashboardPageState } from '../../../hooks/useDashboardPageState';
import {
  AvailabilityMap,
  DateSelection,
  HostAvailabilityRecord,
  HostInfo,
} from '../types';
import { useAvailability } from './useAvailability';
import { AvailabilityTimeSlot, MeetingType } from '../../../models/meeting';
import { AppointmentProviderType } from '../../../models/appointment-type';

type NormalizedAvailabilityEntry = {
  hostInfo: HostInfo;
  slots?: { date: string; times: AvailabilityTimeSlot[] }[];
};

// Coach availability: all time slots belong to a single host (the coach).
// The provider_id inside each slot is a sub-provider handled by the backend
// at booking time — the frontend just groups everything under the coach's id.
function buildCoachAvailabilityMap(
  data: NormalizedAvailabilityEntry[]
): AvailabilityMap {
  return data.reduce<AvailabilityMap>((acc, dataEntry) => {
    dataEntry.slots?.forEach((slot) => {
      const hostId = dataEntry.hostInfo.id;

      if (!acc[slot.date]) acc[slot.date] = {};
      if (!acc[slot.date][hostId]) {
        acc[slot.date][hostId] = { hostInfo: dataEntry.hostInfo, times: [] };
      }

      slot.times.forEach((t) => acc[slot.date][hostId].times.push(t.time));
    });

    return acc;
  }, {});
}

// Provider availability: each entry in data represents one provider.
// Times are keyed by times[].provider_id so the correct provider receives
// each slot — this accounts for cases where a single AvailabilityEntry
// contains times from more than one underlying provider.
function buildProviderAvailabilityMap(
  data: NormalizedAvailabilityEntry[]
): AvailabilityMap {
  const hostInfoById = data.reduce<Record<string, HostInfo>>((acc, entry) => {
    acc[entry.hostInfo.id] = entry.hostInfo;
    return acc;
  }, {});

  return data.reduce<AvailabilityMap>((acc, dataEntry) => {
    dataEntry.slots?.forEach((slot) => {
      slot.times.forEach((t) => {
        const hostId = `${t.provider_id}`;
        const hostInfo = hostInfoById[hostId] ?? dataEntry.hostInfo;

        if (!acc[slot.date]) acc[slot.date] = {};
        if (!acc[slot.date][hostId]) {
          acc[slot.date][hostId] = { hostInfo, times: [] };
        }

        acc[slot.date][hostId].times.push(t.time);
      });
    });

    return acc;
  }, {});
}

const localTimezone = moment.tz.guess();

const createSelectedMonthInitialState = (timezone: string = localTimezone) => {
  const currentMonth = new TZDate(Date.now(), timezone);

  return {
    startDate: currentMonth,
    endDate: computeMonthEndDate(currentMonth, timezone),
  };
};

type Props = {
  careMemberType: AppointmentProviderType;
  meetingType: MeetingType | null;
  providerType: ProviderType | null;
};

export const useDatePickerWithSelectedMeetingType = ({
  careMemberType,
  meetingType,
  providerType,
}: Props) => {
  const {
    state: { user },
  } = useDashboardPageState();

  const didAutomaticallySelectNextMonth = useRef(false);

  const [selectedTimezone, setSelectedTimezone] = useState<'local' | 'user'>(
    'user'
  );

  const timezone =
    selectedTimezone === 'local'
      ? localTimezone
      : user?.timezone || localTimezone;

  const [selectedMonth, setSelectedMonth] = useState<{
    startDate: TZDate;
    endDate: TZDate;
  }>(createSelectedMonthInitialState(timezone));

  const [selectedAvailableDate, setSelectedAvailableDate] =
    useState<DateSelection | null>(null);

  const { data, isError, isLoading, meetingInfo } = useAvailability({
    careMemberType,
    endDate: selectedMonth.endDate,
    meetingType,
    providerType,
    startDate: selectedMonth.startDate,
    timezone,
    user,
  });

  const onMonthChange = (selectedMonth: TZDate) => {
    const endDate = computeMonthEndDate(
      selectedMonth,
      timezone || localTimezone
    );
    setSelectedMonth({ startDate: selectedMonth, endDate });
  };

  const onSelectDate = (newSelectedDate: DateSelection) => {
    setSelectedAvailableDate(newSelectedDate);
  };

  const onTimezoneChange = (newSelectedTimezone: 'local' | 'user') => {
    setSelectedTimezone(newSelectedTimezone);
    setSelectedMonth(createSelectedMonthInitialState(timezone));
    setSelectedAvailableDate(null);
  };

  const jumpToNextMonth = () => {
    const currentMonth = selectedMonth.startDate;
    const nextMonth = new TZDate(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      1,
      timezone
    );
    didAutomaticallySelectNextMonth.current = true;
    onMonthChange(nextMonth);
  };

  const availabilityMap = useMemo<AvailabilityMap>(() => {
    if (!data?.length) return {};

    return providerType === ProviderType.Coach
      ? buildCoachAvailabilityMap(data)
      : buildProviderAvailabilityMap(data);
  }, [data, providerType]);

  // List of meetings of all the hosts within the selected date
  const dayMeetings = useMemo<HostAvailabilityRecord>(() => {
    if (!selectedAvailableDate) return {};

    const dayString = Object.keys(selectedAvailableDate)[0];

    return availabilityMap[dayString];
  }, [availabilityMap, selectedAvailableDate]);

  // It might happen that we are at the end of the month,
  // and we fetch data only for few days. If there's no
  // availability, we need to fetch availability for the
  // next month automatically, until we find a spot.
  // We can do that by updating selectedMonth to trigger
  // a new data request. In that case, only when the component
  // renders the first time, we jump to the month that has the
  // first availability. After that, we leave the users free
  // to navigate the calendar as they want.
  useEffect(() => {
    // This means that if we already redirected the users the first time,
    // next time we have no availability we do nothing (because they
    // might try to just navigate back)
    const didJumpNextMonthWhenNoAvailability =
      isEmpty(availabilityMap) && didAutomaticallySelectNextMonth.current;

    if (isLoading || !data || didJumpNextMonthWhenNoAvailability) return;

    // If, when rendering the first time, we have no availability,
    // we jump to the next month having any availability
    if (isEmpty(availabilityMap) && !didAutomaticallySelectNextMonth.current) {
      jumpToNextMonth();
      return;
    }

    // Here we continue the regular flow, and since we do have availabilities,
    // we select the first day within the current month.
    const [dayString, hostAvailability] =
      Object.entries(availabilityMap)
        .sort()
        .find((entry) => {
          return !!Object.values(entry[1])[0].times.length;
        }) ?? [];

    if (!dayString || !hostAvailability) return;

    const hostId = Object.keys(hostAvailability)[0];
    const timeValues = hostAvailability[hostId].times;

    const date = new TZDate(timeValues[0]);
    const firstAvailableDay = {
      [dayString]: { date, [hostId]: { times: timeValues } },
    } as DateSelection;

    setSelectedAvailableDate(firstAvailableDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want this effect triggering for those props
  }, [isLoading, selectedMonth, timezone]);

  return {
    availabilityMap,
    dayMeetings,
    isError,
    isFetching: isLoading,
    meetingTypeId: meetingInfo?.id ?? '',
    onMonthChange,
    onSelectDate,
    onTimezoneChange,
    selectedDate: !!selectedAvailableDate
      ? Object.values(selectedAvailableDate)[0]['date']
      : new TZDate(Date.now(), timezone),
    selectedMonth,
    selectedTimezone,
    timezone,
  };
};
